/**
 * Komponent WiseRangeButton - uniwersalny komponent do wyboru zakresu wartości
 * Dla date_range: Miesiąc/Kwartał/6 mies./Rok ustawiają ?period=month|quarter|6months|year (domyślnie Rok).
 * ZAKRES ustawia daty _from/_to. Dla range tylko przyciski z wartościami.
 */

import React, { FC, useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { ButtonGroup, Button as ButtonMui } from '@mui/material';
import { DateRangePicker } from 'components/controls';
import classnames from 'classnames';

import { WiseQueryParams } from 'components/wise/types';
import { IDynamicFilter } from 'plugins/api/types';

import styles from 'theme/components/wise/WiseTable/components/RangeButton/RangeButton.module.scss';

export const PERIOD_PARAM = 'period';

export type PeriodValue = 'month' | 'quarter' | '6months' | 'year';

export interface IRangeParameter extends Omit<IDynamicFilter, 'type'> {
  type: 'range' | 'date_range';
}

interface IWiseRangeButtonProps {
  parameter: IRangeParameter;
  queryParams: WiseQueryParams;
  updateParameter: (fieldSymbol: string, value: any) => void;
  showRangeButton?: boolean;
}

type QuickDateRangeType = 'MONTH' | 'QUARTER' | '6_MONTHS' | 'YEAR' | 'RANGE';

const PERIOD_BY_QUICK_TYPE: Record<Exclude<QuickDateRangeType, 'RANGE'>, PeriodValue> = {
  MONTH: 'month',
  QUARTER: 'quarter',
  '6_MONTHS': '6months',
  YEAR: 'year'
};

interface IRangeValue {
  value: string;
  text: string;
}

/**
 * Komponent WiseRangeButton
 */
const WiseRangeButton: FC<IWiseRangeButtonProps> = ({
  parameter,
  queryParams,
  updateParameter,
  showRangeButton = false
}) => {
  const { t } = useTranslation();
  const fieldSymbol = parameter.field_symbol || '';
  const isDateRange = parameter.type === 'date_range';
  const [showDateRangePicker, setShowDateRangePicker] = useState(false);

  // Dla date_range - quick ranges (miesiąc, kwartal, 6 mies., rok)
  const quickDateRanges: Array<{ type: QuickDateRangeType; label: string }> = [
    { type: 'MONTH', label: t('Miesiąc') },
    { type: 'QUARTER', label: t('Kwartal') },
    { type: '6_MONTHS', label: t('6 mies.') },
    { type: 'YEAR', label: t('Rok') }
  ];

  const rangeValues: IRangeValue[] = useMemo(() => {
    if (isDateRange) {
      return [];
    }
    if (parameter.dictionary?.values && Array.isArray(parameter.dictionary.values)) {
      return parameter.dictionary.values.map((item: any) => {
        const value = item.value !== undefined ? String(item.value) : String(item.id || '');
        const text = item.text || item.label || item.name || value;
        return { value, text };
      });
    }
    return [];
  }, [parameter.dictionary, isDateRange]);

  // Dla date_range - pobierz period lub daty
  const period = isDateRange ? (queryParams[PERIOD_PARAM] as PeriodValue | undefined) : undefined;
  const dateFrom = isDateRange ? (queryParams[`${fieldSymbol}_from`] as string | undefined) : undefined;
  const dateTo = isDateRange ? (queryParams[`${fieldSymbol}_to`] as string | undefined) : undefined;

  // Aktywny przycisk: najpierw period (domyślnie Rok), potem RANGE gdy wybrane daty
  const activeDateRange = useMemo<QuickDateRangeType | null>(() => {
    if (!isDateRange) return null;

    if (period) {
      const typeByPeriod: Record<PeriodValue, QuickDateRangeType> = {
        month: 'MONTH',
        quarter: 'QUARTER',
        '6months': '6_MONTHS',
        year: 'YEAR'
      };
      return typeByPeriod[period] ?? null;
    }

    if (dateFrom && dateTo) return 'RANGE';

    // Brak period i dat – traktuj jak domyślny Rok (zaznaczony wizualnie)
    return 'YEAR';
  }, [period, dateFrom, dateTo, isDateRange]);

  // Dla range - określ aktywną wartość
  const activeRangeValue = useMemo<string | null>(() => {
    if (isDateRange) return null;
    const currentValue = queryParams[fieldSymbol];
    if (currentValue === undefined || currentValue === null || currentValue === '') {
      return null;
    }
    return String(currentValue);
  }, [queryParams, fieldSymbol, isDateRange]);

  // Quick range – ustawia ?period=month|quarter|6months|year
  const handleQuickDateRangeClick = (rangeType: QuickDateRangeType) => {
    if (rangeType === 'RANGE') {
      setShowDateRangePicker((prev) => !prev);
      return;
    }

    const periodValue = PERIOD_BY_QUICK_TYPE[rangeType];
    if (!periodValue) return;

    updateParameter(PERIOD_PARAM, periodValue);
    updateParameter(`${fieldSymbol}_from`, '');
    updateParameter(`${fieldSymbol}_to`, '');
    setShowDateRangePicker(false);
  };

  // DateRangePicker – ustawia daty i czyści period
  const handleDateRangePickerChange = (range: { from: string; to: string }) => {
    updateParameter(PERIOD_PARAM, '');
    updateParameter(`${fieldSymbol}_from`, range.from);
    updateParameter(`${fieldSymbol}_to`, range.to);
    setShowDateRangePicker(false);
  };

  // Czyszczenie – przywraca domyślny Rok (period=year)
  const handleClearDateRange = () => {
    updateParameter(PERIOD_PARAM, 'year');
    updateParameter(`${fieldSymbol}_from`, '');
    updateParameter(`${fieldSymbol}_to`, '');
    setShowDateRangePicker(false);
  };

  // Obsługa kliknięcia wartości dla range
  const handleRangeValueClick = (value: string) => {
    // Toggle - jeśli już aktywny, usuń
    updateParameter(fieldSymbol, activeRangeValue === value ? '' : value);
  };

  // Funkcja do czyszczenia wartości dla range
  const handleClearRangeValue = () => {
    updateParameter(fieldSymbol, '');
  };

  // Renderowanie dla date_range
  if (isDateRange) {
    const isDefaultYear = activeDateRange === 'YEAR' && (!period || period === 'year');
    const hasActiveRange = activeDateRange !== null && !isDefaultYear;

    return (
      <div className={styles.componentWrapper}>
        <div className={styles.buttonGroupWrapper}>
          <ButtonGroup
            variant="outlined"
            aria-label="quick date range buttons"
            className={styles.buttonGroup}>
            {/* Przyciski quick ranges */}
            {quickDateRanges.map((range) => (
              <ButtonMui
                key={range.type}
                className={classnames(styles.button, {
                  [styles.active]: activeDateRange === range.type
                })}
                onClick={() => handleQuickDateRangeClick(range.type)}
                variant="outlined">
                {range.label}
              </ButtonMui>
            ))}

            {showRangeButton && (
              <ButtonMui
                className={classnames(styles.button, styles.rangeButton, {
                  [styles.active]: activeDateRange === 'RANGE' || showDateRangePicker
                })}
                onClick={() => handleQuickDateRangeClick('RANGE')}
                variant="outlined">
                {t('ZAKRES')}
              </ButtonMui>
            )}
          </ButtonGroup>

          {/* DateRangePicker popover */}
          {showRangeButton && showDateRangePicker && (
            <div className={styles.dateRangePickerContainer}>
              <DateRangePicker
                dateRange={{
                  from: dateFrom,
                  to: dateTo
                }}
                onChange={handleDateRangePickerChange}
              />
            </div>
          )}
        </div>

        {/* Przycisk wyczyść (jeśli jest aktywny zakres) */}
        {hasActiveRange && (
          <button
            type="button"
            className={styles.clearButton}
            onClick={handleClearDateRange}
            title={t('Wyczyść zakres dat')}>
            ×
          </button>
        )}
      </div>
    );
  }

  // Renderowanie dla range (zwykłe wartości)
  if (rangeValues.length === 0) {
    return null; // Brak wartości do wyświetlenia
  }

  return (
    <div className={styles.componentWrapper}>
      <div className={styles.buttonGroupWrapper}>
        <ButtonGroup
          variant="outlined"
          aria-label="range buttons"
          className={styles.buttonGroup}>
          {rangeValues.map((item) => (
            <ButtonMui
              key={item.value}
              className={classnames(styles.button, {
                [styles.active]: activeRangeValue === item.value
              })}
              onClick={() => handleRangeValueClick(item.value)}
              variant="outlined">
              {item.text}
            </ButtonMui>
          ))}
        </ButtonGroup>
      </div>

      {/* Przycisk wyczyść (jeśli jest aktywna wartość) */}
      {activeRangeValue && (
        <button
          type="button"
          className={styles.clearButton}
          onClick={handleClearRangeValue}
          title={t('Wyczyść')}>
          ×
        </button>
      )}
    </div>
  );
};

export default WiseRangeButton;
